/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../api/api';
import { getTokenData } from 'src/utils/api';
import { LOADING_STATUSES } from '../../../constants/statuses';
import { isResponseSuccess, formatFilters } from '../../../utils/api';
import { INITIAL_FILTERS_STATE } from '../constants/newsPage';

export const loadNewsPage = createAsyncThunk(
  'newsPage/loadNewsPage',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const { filters, page, per } = getState().newsPageReducer;
      const formattedFilters = formatFilters(filters);

      const response = await API(getTokenData()).NEWS.load({
        ...formattedFilters,
        page: page + 1,
        per,
      });
      if (isResponseSuccess(response.status)) {
        return {
          data: response.data,
          totalItems: response.total,
        };
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const loadNewsPageById = createAsyncThunk(
  'newsPage/loadNewsPageById',
  async ({
    id,
  }, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).NEWS.loadById(id);
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const createNewsPage = createAsyncThunk(
  'newsPage/createNewsPage',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).NEWS.create(data);
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const updateNewsPage = createAsyncThunk(
  'newsPage/updateNewsPage',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).NEWS.update(data);
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const deleteNewsPage = createAsyncThunk(
  'newsPage/deleteNewsPage',
  async (id, {
    getState,
    dispatch,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).NEWS.delete(id);
      if (isResponseSuccess(response.status)) {
        dispatch(loadNewsPage());
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

const initialState = {
  entity: [],
  page: 0,
  per: 100,
  totalItems: 0,
  status: LOADING_STATUSES.IDLE,
  error: null,
  selected: {},
  selectedStatus: LOADING_STATUSES.IDLE,
  filters: INITIAL_FILTERS_STATE,
};

const newsPageSlice = createSlice({
  name: 'newsPage',
  initialState,
  reducers: {
    setSelectedNewsPage: (state, action) => {
      state.selected = action.payload;
    },
    setPageData: (state, action) => {
      state.page = action.payload.page;
      state.per = action.payload.per;
    },
    resetNewsPage: (state) => ({
      ...initialState,
      selected: state.selected,
      selectedStatus: state.selectedStatus,
    }),

    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadNewsPage.pending, (state) => {
        state.status = LOADING_STATUSES.LOADING;
      })
      .addCase(loadNewsPage.fulfilled, (state, action) => {
        state.entity = action.payload?.data || [];
        state.totalItems = action.payload?.totalItems || 0;
        state.status = LOADING_STATUSES.SUCCEEDED;
      })
      .addCase(loadNewsPage.rejected, (state) => {
        state.status = LOADING_STATUSES.FAILED;
      })
      .addCase(loadNewsPageById.pending, (state) => {
        state.selectedStatus = LOADING_STATUSES.LOADING;
      })
      .addCase(loadNewsPageById.fulfilled, (state, action) => {
        state.selected = action.payload || {};
        state.selectedStatus = LOADING_STATUSES.SUCCEEDED;
      })
      .addCase(loadNewsPageById.rejected, (state) => {
        state.selectedStatus = LOADING_STATUSES.FAILED;
      });
  },
});

export const {
  setPageData,
  resetNewsPage,
  setSelectedNewsPage,
  setFilters,
} = newsPageSlice.actions;
export default newsPageSlice.reducer;

export const newsPageChangePageAction = createAsyncThunk(
  'newsPage/newsPageChangePageAction',
  async ({
    page = 0,
    per = 0,
  }, {
    dispatch,
    rejectWithValue,
  }) => {
    try {
      await dispatch(setPageData({
        page,
        per,
      }));
      await dispatch(loadNewsPage());
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const newsPageFilterApply = createAsyncThunk(
  'newsPage/filterApply',
  async (newFilters, {
    dispatch,
    rejectWithValue,
  }) => {
    try {
      await dispatch(setFilters({
        ...newFilters,
      }));
      await dispatch(loadNewsPage());
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);
